import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PatientDetail, TriageSubmission } from "@/hooks/usePatientDetail";
import { AlertTriangle, Calendar, Activity, Heart, Thermometer, Clock } from "lucide-react";
import { format, parseISO, isAfter } from "date-fns";

interface ClinicalIndicatorsCardProps {
  patient: PatientDetail;
}

export function ClinicalIndicatorsCard({ patient }: ClinicalIndicatorsCardProps) {
  // Find latest triage submission
  const latestTriage = patient.triageHistory.length > 0 
    ? patient.triageHistory.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0] 
    : null;
  
  // Check if patient has any urgent triage history
  const hasUrgentTriage = patient.triageHistory.some(
    triage => triage.urgency === 'immediate' || triage.urgency === 'urgent'
  );
  
  // Find upcoming appointment
  const now = new Date();
  const upcomingAppointment = patient.appointments
    .filter(appointment => isAfter(parseISO(appointment.start_time), now))
    .sort((a, b) => 
      parseISO(a.start_time).getTime() - parseISO(b.start_time).getTime()
    )[0];
  
  // Format date
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "Not available";
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch (e) {
      return dateString || "Not available";
    }
  };

  // Get vital signs status
  const getVitalSignsStatus = () => {
    if (!latestTriage?.vital_signs) return { status: "none", message: "No vital signs recorded" };
    
    const vitals = latestTriage.vital_signs;
    
    // Check for concerning vital signs
    const concerns = [];
    
    if (vitals.temperature && (vitals.temperature > 38.5 || vitals.temperature < 35.5)) {
      concerns.push("Abnormal temperature");
    }
    
    if (vitals.heart_rate && (vitals.heart_rate > 100 || vitals.heart_rate < 60)) {
      concerns.push("Abnormal heart rate");
    }
    
    if (vitals.blood_pressure_systolic && vitals.blood_pressure_diastolic) {
      if (vitals.blood_pressure_systolic > 140 || vitals.blood_pressure_diastolic > 90) {
        concerns.push("Elevated blood pressure");
      } else if (vitals.blood_pressure_systolic < 90 || vitals.blood_pressure_diastolic < 60) {
        concerns.push("Low blood pressure");
      }
    }
    
    if (vitals.oxygen_saturation && vitals.oxygen_saturation < 95) {
      concerns.push("Low oxygen saturation");
    }
    
    if (concerns.length > 0) {
      return { 
        status: "concern", 
        message: concerns.join(", ")
      };
    }
    
    return { 
      status: "normal", 
      message: "Last recorded on " + formatDate(latestTriage.created_at)
    };
  };
  
  const vitalSignsInfo = getVitalSignsStatus();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Clinical Indicators</CardTitle>
        <CardDescription>Key health metrics and alerts</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Urgency Level */}
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${hasUrgentTriage ? 'bg-red-100' : 'bg-gray-100'}`}>
            <AlertTriangle className={`h-5 w-5 ${hasUrgentTriage ? 'text-red-500' : 'text-gray-500'}`} />
          </div>
          <div>
            <p className="font-medium">Urgency Level</p>
            <p className="text-sm text-gray-600">
              {hasUrgentTriage 
                ? "Patient has urgent triage history" 
                : "No urgent triage history"}
            </p>
          </div>
        </div>
        
        {/* Next Appointment */}
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${upcomingAppointment ? 'bg-blue-100' : 'bg-gray-100'}`}>
            <Calendar className={`h-5 w-5 ${upcomingAppointment ? 'text-blue-500' : 'text-gray-500'}`} />
          </div>
          <div>
            <p className="font-medium">Next Appointment</p>
            <p className="text-sm text-gray-600">
              {upcomingAppointment 
                ? formatDate(upcomingAppointment.start_time)
                : "No upcoming appointments"}
            </p>
          </div>
        </div>
        
        {/* Vital Signs */}
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${
            vitalSignsInfo.status === 'concern' ? 'bg-amber-100' : 
            vitalSignsInfo.status === 'normal' ? 'bg-green-100' : 'bg-gray-100'
          }`}>
            <Activity className={`h-5 w-5 ${
              vitalSignsInfo.status === 'concern' ? 'text-amber-500' : 
              vitalSignsInfo.status === 'normal' ? 'text-green-500' : 'text-gray-500'
            }`} />
          </div>
          <div>
            <p className="font-medium">Vital Signs</p>
            <p className="text-sm text-gray-600">
              {vitalSignsInfo.message}
            </p>
          </div>
        </div>

        {/* Latest Triage */}
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${latestTriage ? 'bg-purple-100' : 'bg-gray-100'}`}>
            <Clock className={`h-5 w-5 ${latestTriage ? 'text-purple-500' : 'text-gray-500'}`} />
          </div>
          <div>
            <p className="font-medium">Latest Triage</p>
            <p className="text-sm text-gray-600">
              {latestTriage 
                ? formatDate(latestTriage.created_at)
                : "No triage history"}
            </p>
          </div>
        </div>

        {/* Medical Conditions */}
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${patient.medical_history ? 'bg-indigo-100' : 'bg-gray-100'}`}>
            <Heart className={`h-5 w-5 ${patient.medical_history ? 'text-indigo-500' : 'text-gray-500'}`} />
          </div>
          <div>
            <p className="font-medium">Medical Conditions</p>
            <p className="text-sm text-gray-600">
              {patient.medical_history 
                ? (patient.medical_history.length > 30 
                  ? patient.medical_history.substring(0, 30) + "..." 
                  : patient.medical_history)
                : "No conditions recorded"}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
